import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { GLOBAL_STEP_ORDER } from '../../../shared/constants/flowConfig';
import {
  OPERATION_LOG_SCHEMA_VERSION,
  createOperationDescriptor,
} from './operationDescriptor';

/**
 * Ordered operation log keyed by step. Descriptors are lightweight + serializable,
 * so unlike the mesh pool this can safely live in Zustand and be persisted to the
 * server operations manifest.
 *
 * @typedef {import('./operationDescriptor').OperationDescriptor} OperationDescriptor
 */

const useOperationLogStore = create(
  devtools((set, get) => ({
    schemaVersion: OPERATION_LOG_SCHEMA_VERSION,

    /** @type {Record<string, OperationDescriptor[]>} */
    operationsByStep: {},

    /** Append one applied operation to a step's log. */
    recordOperation: (stepId, toolId, params, metrics) =>
      set((state) => {
        const existing = state.operationsByStep[stepId] ?? [];
        const descriptor = createOperationDescriptor(
          toolId,
          stepId,
          params,
          existing.length,
          metrics,
        );
        return {
          operationsByStep: {
            ...state.operationsByStep,
            [stepId]: [...existing, descriptor],
          },
        };
      }),

    getOperationsForStep: (stepId) => get().operationsByStep[stepId] ?? [],

    /** Flattened, step-ordered list of every operation up to and including `stepId`. */
    getOperationsUpTo: (stepId) => {
      const targetIdx = GLOBAL_STEP_ORDER.indexOf(stepId);
      if (targetIdx < 0) return [];
      const { operationsByStep } = get();
      const out = [];
      for (let i = 0; i <= targetIdx; i++) {
        const step = GLOBAL_STEP_ORDER[i];
        const ops = operationsByStep[step];
        if (ops?.length) out.push(...ops);
      }
      return out;
    },

    /** Drop logs for every step strictly after `stepId` (backward revert). */
    clearOperationsAfter: (stepId) =>
      set((state) => {
        const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
        if (idx < 0) return state;
        const keep = new Set(GLOBAL_STEP_ORDER.slice(0, idx + 1));
        const next = {};
        for (const [step, ops] of Object.entries(state.operationsByStep)) {
          if (keep.has(step)) next[step] = ops;
        }
        return { operationsByStep: next };
      }),

    clearOperationsForStep: (stepId) =>
      set((state) => {
        if (!state.operationsByStep[stepId]) return state;
        const { [stepId]: _drop, ...rest } = state.operationsByStep;
        return { operationsByStep: rest };
      }),

    /** Replace the whole log (used when hydrating from a server manifest). */
    hydrateFromManifest: (operationsByStep) =>
      set({ operationsByStep: operationsByStep ?? {} }),

    clearAll: () => set({ operationsByStep: {} }),
  })),
);

export default useOperationLogStore;
